/**
 * Chat / Mod — live lobby chat feed with quick moderation controls.
 *
 * Send / receive piggy-backs on the existing wiring in `client/src/chat`:
 *   - `useStore.chatLog` is the schema-mirrored ArraySchema feed
 *   - `sendChat(body)` ships a `{ body }` message to the current room
 *
 * Mod actions are *client-side only* (mute, hide, highlight, clear local
 * view) — see `devChatMod.ts` for why. A "Copy log" button drops the full
 * visible feed into the clipboard so we can paste it into bug reports.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";

import { sendChat } from "../../chat/wire";
import { useStore } from "../../store";
import { useDevChatMod } from "../store/devChatMod";
import { btnStyle, colors, copyToClipboard, styles } from "./_shared";

export function ChatModSection(): JSX.Element {
  const chatLog = useStore((s) => s.chatLog);
  const currentRoom = useStore((s) => s.currentRoom);
  const muted = useDevChatMod((s) => s.mutedUsers);
  const hidden = useDevChatMod((s) => s.hiddenIds);
  const highlight = useDevChatMod((s) => s.highlight);
  const muteUser = useDevChatMod((s) => s.muteUser);
  const unmuteUser = useDevChatMod((s) => s.unmuteUser);
  const hideMessage = useDevChatMod((s) => s.hideMessage);
  const clearHidden = useDevChatMod((s) => s.clearHidden);
  const setHighlight = useDevChatMod((s) => s.setHighlight);

  const [draft, setDraft] = useState("");
  const [showHidden, setShowHidden] = useState(false);
  const feedRef = useRef<HTMLDivElement>(null);

  const visible = useMemo(
    () =>
      chatLog.filter((m) => {
        if (muted.has(m.from.toLowerCase())) return showHidden;
        if (hidden.has(m.id)) return showHidden;
        return true;
      }),
    [chatLog, muted, hidden, showHidden],
  );

  // Auto-scroll to bottom on new messages.
  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [visible.length]);

  const onSend = () => {
    if (!draft.trim()) return;
    sendChat(draft);
    setDraft("");
  };

  const copyLog = async () => {
    const text = visible.map((m) => `[${new Date(m.ts).toISOString()}] ${m.from}: ${m.body}`).join("\n");
    await copyToClipboard(text);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={styles.sectionHeader}>
        <div>
          <div style={styles.sectionTitle}>Chat / Mod</div>
          <div style={styles.sectionSubtitle}>
            {currentRoom ? "connected to lobby" : "not connected — chat will silently drop"} ·
            client-side mod only (mutes reset on reload)
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => void copyLog()} style={btnStyle({ tone: "ghost", small: true })}>
            Copy log
          </button>
          <button
            onClick={() => setShowHidden((v) => !v)}
            style={btnStyle({ tone: "ghost", small: true })}
          >
            {showHidden ? "Hide muted" : "Show muted"}
          </button>
          <button onClick={clearHidden} style={btnStyle({ tone: "danger", small: true })}>
            Clear mod state
          </button>
        </div>
      </div>

      <div style={{ padding: 12, paddingBottom: 6 }}>
        <div style={styles.row}>
          <input
            value={highlight}
            onChange={(e) => setHighlight(e.target.value)}
            placeholder="highlight substring (e.g. lfg)"
            style={{ ...styles.inputStyle, flex: "0 0 240px" }}
          />
          {muted.size > 0 && (
            <span style={{ ...styles.mono, color: colors.warn, fontSize: 11 }}>
              muted: {Array.from(muted).join(", ")}
            </span>
          )}
        </div>
      </div>

      <div ref={feedRef} style={feedStyle}>
        {visible.length === 0 ? (
          <div style={{ padding: 12, ...styles.mono, color: colors.textMute }}>
            (no chat yet — type below to send as the connected wallet)
          </div>
        ) : (
          visible.map((m) => {
            const isMuted = muted.has(m.from.toLowerCase());
            const isHidden = hidden.has(m.id);
            const isHi = highlight && m.body.toLowerCase().includes(highlight);
            const ts = new Date(m.ts);
            const hh = ts.getHours().toString().padStart(2, "0");
            const mm = ts.getMinutes().toString().padStart(2, "0");
            const ss = ts.getSeconds().toString().padStart(2, "0");
            return (
              <div
                key={m.id}
                style={{
                  ...msgRowStyle,
                  opacity: isMuted || isHidden ? 0.4 : 1,
                  background: isHi ? "rgba(245,198,116,0.08)" : undefined,
                }}
              >
                <span style={{ color: colors.textFaint, fontSize: 10, flex: "0 0 60px" }}>
                  {hh}:{mm}:{ss}
                </span>
                <span style={{ color: colors.accent, fontWeight: 700, fontSize: 11, flex: "0 0 140px", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {m.from}
                </span>
                <span style={{ ...styles.mono, flex: 1, color: colors.text }}>
                  {m.body}
                </span>
                <div style={{ display: "flex", gap: 4 }}>
                  {isMuted ? (
                    <button
                      onClick={() => unmuteUser(m.from)}
                      style={btnStyle({ tone: "ghost", small: true })}
                    >
                      Unmute
                    </button>
                  ) : (
                    <button
                      onClick={() => muteUser(m.from)}
                      style={btnStyle({ tone: "ghost", small: true })}
                      title="Hide all messages from this user (this browser only)"
                    >
                      Mute
                    </button>
                  )}
                  <button
                    onClick={() => hideMessage(m.id)}
                    style={btnStyle({ tone: "ghost", small: true })}
                    disabled={isHidden}
                  >
                    Hide
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div style={composerStyle}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
          }}
          placeholder={currentRoom ? "say something… (enter to send)" : "join lobby to chat"}
          disabled={!currentRoom}
          style={{ ...styles.inputStyle, fontFamily: "inherit", fontSize: 12 }}
          maxLength={280}
        />
        <button
          onClick={onSend}
          style={btnStyle({ tone: "primary", disabled: !currentRoom || !draft.trim() })}
          disabled={!currentRoom || !draft.trim()}
        >
          Send
        </button>
      </div>
    </div>
  );
}

const feedStyle: React.CSSProperties = {
  flex: 1,
  overflowY: "auto",
  borderTop: `1px solid ${colors.border}`,
  borderBottom: `1px solid ${colors.border}`,
  background: colors.bgRaised,
  minHeight: 0,
};

const msgRowStyle: React.CSSProperties = {
  display: "flex",
  gap: 8,
  alignItems: "center",
  padding: "4px 12px",
  borderBottom: `1px solid ${colors.border}`,
};

const composerStyle: React.CSSProperties = {
  display: "flex",
  gap: 6,
  padding: 12,
  background: colors.bg,
};
